import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const MOCK_KPIS = [
  { label: 'Active Equipment', value: 8, unit: 'units', trend: '+2 today' },
  { label: 'Acres Completed', value: 1247, unit: 'ac', trend: '+312 today' },
  { label: 'Crew On Shift', value: 12, unit: 'members', trend: '3 shifts active' },
  { label: 'Operations Running', value: 5, unit: 'ops', trend: '2 ahead of schedule' },
  { label: 'Fuel Consumed', value: 1840, unit: 'gal', trend: 'avg 4.2 gal/ac' },
  { label: 'Uptime', value: 94.7, unit: '%', trend: '1 unit in maintenance' },
];

const MOCK_EQUIPMENT = [
  { id: 'JD-8R-410', name: '8R 410 Tractor', status: 'running', field: 'North Quarter', operator: 'Jake Morrison', speed: '5.2 mph', fuel: 72, acres: 48.3 },
  { id: 'JD-S790', name: 'S790 Combine', status: 'idle', field: 'South 160', operator: 'Unassigned', speed: '0 mph', fuel: 88, acres: 0 },
  { id: 'JD-9RX-640', name: '9RX 640 Tractor', status: 'running', field: 'East Bottoms', operator: 'Maria Santos', speed: '4.8 mph', fuel: 55, acres: 62.1 },
  { id: 'JD-1795', name: '1795 Planter', status: 'running', field: 'North Quarter', operator: 'Jake Morrison', speed: '5.2 mph', fuel: null, acres: 48.3 },
  { id: 'JD-CH570', name: 'CH570 Windrower', status: 'maintenance', field: 'Shop', operator: 'N/A', speed: '0 mph', fuel: 40, acres: 0 },
  { id: 'CASE-4440', name: 'Case 4440 Sprayer', status: 'running', field: 'West 80', operator: 'Tom Becker', speed: '8.1 mph', fuel: 63, acres: 34.5 },
  { id: 'JD-560M', name: '560M Baler', status: 'idle', field: 'Barn', operator: 'Unassigned', speed: '0 mph', fuel: 91, acres: 0 },
  { id: 'JD-8R-370', name: '8R 370 Tractor', status: 'running', field: 'River Field', operator: 'Sam Hutchins', speed: '6.0 mph', fuel: 48, acres: 55.7 },
];

const MOCK_CREW = [
  { name: 'Jake Morrison', role: 'Operator', shift: 'Day', status: 'active', equipment: '8R 410 + 1795 Planter', hours: 6.5 },
  { name: 'Maria Santos', role: 'Operator', shift: 'Day', status: 'active', equipment: '9RX 640 Tractor', hours: 7.0 },
  { name: 'Tom Becker', role: 'Operator', shift: 'Day', status: 'active', equipment: 'Case 4440 Sprayer', hours: 5.2 },
  { name: 'Sam Hutchins', role: 'Operator', shift: 'Day', status: 'active', equipment: '8R 370 Tractor', hours: 4.8 },
  { name: 'Lisa Tran', role: 'Mechanic', shift: 'Day', status: 'active', equipment: 'CH570 Windrower (repair)', hours: 3.0 },
  { name: 'Derek Hall', role: 'Supervisor', shift: 'Day', status: 'active', equipment: 'Truck - Field Scout', hours: 7.5 },
];

const MOCK_ALERTS = [
  { id: 1, severity: 'warning', message: 'CH570 Windrower - hydraulic pressure low, maintenance in progress', time: '14 min ago' },
  { id: 2, severity: 'info', message: 'North Quarter planting 87% complete - ETA 2:15 PM', time: '22 min ago' },
  { id: 3, severity: 'warning', message: '8R 370 fuel below 50% - refuel recommended at end of pass', time: '35 min ago' },
  { id: 4, severity: 'success', message: 'East Bottoms tillage completed ahead of schedule', time: '1 hr ago' },
  { id: 5, severity: 'info', message: 'Weather: Clear skies, wind 8 mph SW - good conditions', time: '1 hr ago' },
];

function OverwatchPage() {
  const [kpis] = useState(MOCK_KPIS);
  const [equipment] = useState(MOCK_EQUIPMENT);
  const [crew] = useState(MOCK_CREW);
  const [alerts] = useState(MOCK_ALERTS);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setLastUpdate(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusBadge = (status) => {
    const map = { running: 'badge-success', active: 'badge-success', idle: 'badge-warning', maintenance: 'badge-danger' };
    return map[status] || 'badge-default';
  };

  const getAlertStyle = (severity) => {
    const map = {
      warning: { borderLeft: '4px solid #FFDE00', background: 'rgba(255, 222, 0, 0.08)' },
      info: { borderLeft: '4px solid #4A90D9', background: 'rgba(74, 144, 217, 0.08)' },
      success: { borderLeft: '4px solid #367C2B', background: 'rgba(54, 124, 43, 0.08)' },
      danger: { borderLeft: '4px solid #D9534F', background: 'rgba(217, 83, 79, 0.08)' },
    };
    return map[severity] || map.info;
  };

  const thStyle = { padding: '10px 12px', fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.6 };
  const tdStyle = { padding: '10px 12px', fontSize: '0.85rem' };

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Overwatch</h1>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85rem' }}>
            Live Farm Operations Overview &mdash; Updated: {lastUpdate.toLocaleTimeString()}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setLastUpdate(new Date())}>Refresh</button>
      </div>

      {/* KPI Strip */}
      <div className="stats-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
        {kpis.map((kpi, idx) => (
          <div className="stat-card" key={idx}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.5px' }}>{kpi.label}</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: '#367C2B', margin: '4px 0' }}>
              {typeof kpi.value === 'number' && kpi.value > 100 ? kpi.value.toLocaleString() : kpi.value}
              <span style={{ fontSize: '0.8rem', fontWeight: 400, marginLeft: '4px', opacity: 0.7 }}>{kpi.unit}</span>
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>{kpi.trend}</div>
          </div>
        ))}
      </div>

      {/* Live Alerts */}
      <div className="card" style={{ marginTop: "16px" }}>
        <h3 style={{ margin: '0 0 12px' }}>Live Alerts</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {alerts.map((alert) => (
            <div key={alert.id} style={{ ...getAlertStyle(alert.severity), padding: "10px 14px", borderRadius: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: '0.9rem' }}>{alert.message}</span>
              <span style={{ fontSize: "0.75rem", opacity: 0.5, whiteSpace: "nowrap", marginLeft: "16px" }}>{alert.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Equipment Status */}
      <div className="card" style={{ marginTop: "16px" }}>
        <h3 style={{ margin: '0 0 12px' }}>Equipment Status</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", textAlign: "left" }}>
                <th style={thStyle}>Unit ID</th>
                <th style={thStyle}>Equipment</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Operator</th>
                <th style={thStyle}>Speed</th>
                <th style={thStyle}>Fuel</th>
                <th style={thStyle}>Acres Today</th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((eq) => (
                <tr key={eq.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{eq.id}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{eq.name}</td>
                  <td style={tdStyle}><span className={getStatusBadge(eq.status)}>{eq.status}</span></td>
                  <td style={tdStyle}>{eq.field}</td>
                  <td style={tdStyle}>{eq.operator}</td>
                  <td style={tdStyle}>{eq.speed}</td>
                  <td style={tdStyle}>
                    {eq.fuel !== null ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: "60px", height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "3px" }}>
                          <div style={{ width: eq.fuel + "%", height: "100%", borderRadius: "3px", background: eq.fuel > 50 ? "#367C2B" : eq.fuel > 25 ? "#FFDE00" : "#D9534F" }} />
                        </div>
                        <span style={{ fontSize: '0.8rem' }}>{eq.fuel}%</span>
                      </div>
                    ) : <span style={{ fontSize: '0.8rem', opacity: 0.4 }}>N/A</span>}
                  </td>
                  <td style={tdStyle}>{eq.acres > 0 ? eq.acres + ' ac' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Crew On Shift */}
      <div className="card" style={{ marginTop: "16px" }}>
        <h3 style={{ margin: '0 0 12px' }}>Crew On Shift</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", textAlign: "left" }}>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Shift</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Equipment / Task</th>
                <th style={thStyle}>Hours Today</th>
              </tr>
            </thead>
            <tbody>
              {crew.map((m, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{m.name}</td>
                  <td style={tdStyle}>{m.role}</td>
                  <td style={tdStyle}>{m.shift}</td>
                  <td style={tdStyle}><span className={getStatusBadge(m.status)}>{m.status}</span></td>
                  <td style={tdStyle}>{m.equipment}</td>
                  <td style={tdStyle}>{m.hours} hrs</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default OverwatchPage;